<template>
	<AppContent ref="content">
		<ContentHeader :lockedCollectionType="CollectionType.POPULAR" />

		<div v-if="!isProviderPage">
			<div v-if="!perceivedLoading || !isProviderPageChanged">
				<LazyFilterBar
					v-if="isLoggedIn"
					:collectionType="CollectionType.POPULAR"
					:currentFilters="filterPopular"
					:itemsCount="titleAmount"
				/>
				<LazyFilterBarSeo v-else :collectionType="CollectionType.POPULAR" :currentFilters="filterPopular" />
			</div>
		</div>

		<!-- BI_SCROLL_TOP_OFFSET_PLACEMENT_ID is used here to ensure accurate scroll position calculation for updating the query parameter.
			 This is necessary because this module introduces an additional offset at the top of the page. -->
		<div v-if="isProviderPage" class="content-provider-content" :data-page="BI_SCROLL_TOP_OFFSET_PLACEMENT_ID">
			<div v-if="!perceivedLoading || !isProviderPageChanged">
				<ProviderModule
					:collectionType="CollectionType.POPULAR"
					:currentFilters="filterPopular"
					:provider="selectedProvider"
					:selectedTab="selectedTab"
					:amount="titleAmount"
				/>

				<div id="catalog-jumplink"></div>

				<LazyPopularFilterBar
					class="container-fluid container-max-width filter-bar"
					:currentFilters="filterPopular"
					:titleCount="titleAmount"
					:selectedTab="selectedTab"
					:provider="selectedProvider"
					:searchText="searchText"
					:isProviderPage="isProviderPage"
					:activeSubgenreTitle="activeSubgenreTitle"
					@search="search"
				/>
			</div>
		</div>

		<TitleList
			:collectionType="CollectionType.POPULAR"
			:currentFilters="filterPopular"
			:is-at-end-of-list="isAtEndOfList"
			:is-first-page="isFirstPage"
			:list-layout="TitleListLayout.GRID"
			:sponsoredAd="sponsoredAd"
			:titles-amount="titleAmount"
			:titles="popularTitles"
			:titlesPerPage="queryVariables.first"
			:pagination-level="paginationLevel"
			:loading="false"
			placement="popular_page"
			@load-next="({ event, page }) => load(event, page, 'next')"
			@load-prev="({ event, page }) => load(event, page, 'prev')"
			@show-fallbacks="showFooter = true"
			@onPosterClick="trackClick"
			@onScrollToTop="scrollToTop"
		>
			<template #list-options v-if="!isLoggedIn">
				<div class="list-options" v-if="!isProviderPage">
					<TitleListOptionsSeo
						v-if="!perceivedLoading"
						:collection-type="CollectionType.POPULAR"
						:amount="titleAmount"
					>
					</TitleListOptionsSeo>

					<FilterBarSubgenrePills
						v-if="showSubgenresFilter && orderedSubgenres && subgenresList"
						:orderedSubgenres="orderedSubgenres"
						:subgenresList="subgenresList"
					/>
				</div>
			</template>

			<template #spinner>
				<ClientOnly>
					<div v-if="!isFirstPageLoaded && perceivedLoading" class="popular-page__spinner">
						<Spinner color="light" name="crescent" />
					</div>
				</ClientOnly>
			</template>

			<template #sponsoredRecommendation>
				<SponsoredRecommendation
					v-if="sponsoredAd"
					:lazy="false"
					:key="sponsoredAd?.bidId"
					:sponsoredAd="sponsoredAd"
					placement="sr_popular"
					:sraPlacement="SraPlacement.PopularView"
					@onSponsoredRecommendationClick="trackClick"
				/>
			</template>
		</TitleList>

		<!-- Sub components of AppFooter NuxtLazyHydrate -->
		<AppFooter v-show="(showFooter || isAtEndOfList) && !perceivedLoading" />
	</AppContent>
</template>

<script setup lang="ts">
import { AdPlacement, MonetizationType, PopularTitlesSorting, SraPlacement, TitleFilter } from '@/@types/graphql-types';
import type AppContent from '@/components/AppContent.vue';
import { equal as isEqual } from '@wry/equality';
import { storeToRefs } from 'pinia';

import { TitleList } from '@/lazy-load-components';

import ContentHeader from '@/components/content-header/ContentHeader.vue';
import SponsoredRecommendation from '@/features/sponsored-recommendations/components/SponsoredRecommendation.vue';
import Spinner from '@/ui-components/Spinner.vue';

import { CollectionType } from '@/enums/collection-type';
import { TitleListLayout } from '@/enums/title-list-layout';
import { TitleOrder } from '@/enums/title-order';

import { toGraphqlFilters } from '@/helpers/graphql-model-helper';
import { handleStripeRedirect } from '@/helpers/pro-web-helper';
import { captureMessageForSentry } from '@/libraries/sentry';
import { TitleEventPayloadGraphql, trackTitleEventGraphql } from '@/helpers/title-helper';

import { toastError } from '@/helpers/vfm/toast-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import {
	GetPopularMetaDataDocument,
	GetPopularMetaDataQuery,
	GetPopularMetaDataQueryVariables,
	GetPopularTitlesDocument,
	GetPopularTitlesQuery,
	GetPopularTitlesQueryVariables,
} from '@/pages/graphql/queries/GetPopularTitles.query';

import { useSendBidId } from '@/features/sponsored-recommendations/composables/useSendBidId';
import { useGetSrPlacement } from '@/features/sponsored-recommendations/composables/useGetSrPlacement';
import adminConfig from '@/features/promotions/config.json';
import { parsePageNumber } from '@/helpers/composables/usePagination';

import { hasQueryParamFilters, isFilterActive } from '@/helpers/filter-helper';
import { PopularTabType, TitleObjectType } from '@/interfaces/titles';

import { getSelectedTab } from '@/components/popular/provider-module/helper';

// need to import directly, otherwise we run into CLS issues.
// with <Suspense> in vue 3 we can better handle this, but for now it's too finicky.
import FilterBarSubgenrePills from '@/components/filter-bar/FilterBarSubgenrePills.vue';
const LazyPopularFilterBar = defineLazyHydrationComponent(
	'idle',
	() => import('@/components/popular/provider-module/PopularFilterBar.vue')
);
import ProviderModule from '@/components/popular/provider-module/ProviderModule.vue';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
	GetOrderedSubgenresDocument,
	GetOrderedSubgenresQuery,
	GetOrderedSubgenresQueryVariables,
} from './graphql/queries/GetSubgenres.query';

import { activeSubgenreCountries } from '@/constants/subgenres';
import { BI_SCROLL_TOP_OFFSET_PLACEMENT_ID } from '@/helpers/composables/useBiScroll';
import { PAGINATION_LEVELS, useMetaStore } from '@/stores/pinia/useMetaStore';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { RoutingState, useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useFilterStore } from '@/stores/pinia/useFilterStore';
import type { FilterCollection } from '@/stores/useFilterStoreTypes';

const LazyFilterBar = defineLazyHydrationComponent('idle', () => import('@/components/filter-bar/FilterBar.vue'));
const LazyFilterBarSeo = defineLazyHydrationComponent(
	'idle',
	() => import('@/components/filter-bar/seo/FilterBarSeo.vue')
);
const TitleListOptionsSeo = defineAsyncComponent(() => import('@/components/filter-bar/seo/TitleListOptionsSeo.vue'));

const { t } = useI18n();
const route = useRoute();

const fullPath = computed(() => route.fullPath);

const showFooter = ref(false);
const requestPage = ref(getCurrentPage());

// the same as `activeRoute` but only changes when the new page is also CollectionType.POPULAR.
// it doesn't change, unlike `activeRoute`, when one leaves the popular page.
// this way we can circument re-renders in the background.
const popularRoute = ref<RoutingState['activeRoute'] | null>(null);

const { filters, selectedProviders } = useFilterStore();
const filterPopular = computed(() => filters(CollectionType.POPULAR));

const { language, country } = storeToRefs(useLanguageStore());

// ** SRs Adserver Migration ** //
const popularSrConfig = adminConfig?.ad_server?.srs?.placements?.popular_view_page;
const useAdserverSrMutation = computed(
	() => popularSrConfig?.all_countries || popularSrConfig?.countries?.includes(country.value)
);

const { sponsoredAd } = useAdserverSrMutation.value
	? useGetSrPlacement({
			pageType: 'VIEW_POPULAR',
			placement: SraPlacement.PopularView,
			adPlacement: AdPlacement.PopularViewSr,
	  })
	: useSendBidId({
			pageType: 'VIEW_POPULAR',
			placement: SraPlacement.PopularView,
	  });

const { queryParams, activeRoute, lastRoute, activeDomain } = storeToRefs(useRoutingStore());

const userStore = useUserStore();
const { setPermanentAudiencesSubgenres, resetPermanentAudiencesSubgenres } = userStore;
const { isHideSeenDislikedTitlesEnabled, isLoggedIn, jwLoginId } = storeToRefs(userStore);

const metaStore = useMetaStore();
const { setPagination, setPopularPageJsonLD } = metaStore;
const { paginationLevel } = storeToRefs(metaStore);

const content = ref<typeof AppContent | null>(null);

const searchText = ref('');

const isProviderPage = computed(
	() =>
		!!popularRoute.value?.name?.startsWith('app.titles.popular.provider') &&
		!!selectedProvider.value &&
		isProviderPagesEnabled.value
);

const hasPaginationImplementation = computed(() => {
	// hard page limit through levels is happening as well on InfiniteBiScroll.vue:40
	const hardLimit = paginationLevel.value === 0 ? 0 : PAGINATION_LEVELS[paginationLevel.value].pages;
	const isWithinHardLimit = getCurrentPage() <= hardLimit;
	const hasQueryParams = hasQueryParamFilters(filterPopular.value);
	return paginationLevel.value > 0 && isWithinHardLimit && !hasQueryParams;
});

// Generic Title List
const graphqlFilters = computed<TitleFilter>(() =>
	toGraphqlFilters(filterPopular.value, selectedProviders(!isLoggedIn.value), isHideSeenDislikedTitlesEnabled.value)
);

// Sort By options from GraphQL are Uppercase, but tracking expects lowercase TODO [Graphql] remove when get rid of REST
const RestToGraphQLSortingMap: Partial<{ [key in TitleOrder]: PopularTitlesSorting }> = {
	[TitleOrder.POPULARITY]: PopularTitlesSorting.Popular,
	[TitleOrder.TRENDING]: PopularTitlesSorting.Trending,
	[TitleOrder.TMDB_POPULARITY]: PopularTitlesSorting.TmdbPopularity,
	[TitleOrder.ALPHABETICAL]: PopularTitlesSorting.Alphabetical,
	[TitleOrder.RANDOM]: PopularTitlesSorting.Random,
	[TitleOrder.RELEASE_YEAR]: PopularTitlesSorting.ReleaseYear,
	[TitleOrder.RELEVANCE]: PopularTitlesSorting.Relevance,
	[TitleOrder.SCORE_IMDB]: PopularTitlesSorting.ImdbScore,
	[TitleOrder.RUNTIME]: PopularTitlesSorting.Runtime,
};
const sortBy = computed<PopularTitlesSorting>(() => {
	// To fetch titles on server side we need to get sort by option from url params
	if (queryParams.value.sort_by) {
		return RestToGraphQLSortingMap[queryParams.value.sort_by as TitleOrder] ?? PopularTitlesSorting.Popular;
	}

	if (filterPopular.value.sort_by) {
		return RestToGraphQLSortingMap[filterPopular.value.sort_by as TitleOrder] ?? PopularTitlesSorting.Popular;
	}

	return PopularTitlesSorting.Popular;
});

// Popular Page Data
/**
 * Popular page initially renders with "page 0" for a fast first experience.
 * On first scroll we then fetch the "page 1", this triggers loading meaning
 * the whole grid disappears for a spinner upon first interaction.
 *
 * This variable is to have an exception for that first load.
 */
const isFirstPageLoaded = ref(false);

const randomSeed = computed(() => {
	const isRandomSort = sortBy.value === PopularTitlesSorting.Random;

	if (import.meta.server) {
		const randomSeed = Number(queryParams.value.sorting_random_seed) || 1;
		return isRandomSort ? randomSeed : 0;
	}

	const randomSeed = filterPopular.value.sorting_random_seed || 1;
	return isRandomSort ? randomSeed : 0;
});

const queryVariables = computed<GetPopularTitlesQueryVariables>(() => {
	// SEO benefits from more titles per page but that hurts perf for returning users
	// plus, Google started scraping (unnecessarily) pages with query params that take much longer SSR'ing on-demand,
	// so Google adds the longer request times to its statistics and crawls us less.
	const hasQueryParams = hasQueryParamFilters(filterPopular.value);
	const loggedOutAmount = hasQueryParams ? 100 : 40;
	const first = isLoggedIn.value ? 40 : loggedOutAmount;
	const offset = (requestPage.value - 1) * first;

	return {
		first,
		offset: hasPaginationImplementation.value ? offset : null, // use offset for new <InfiniteBiScroll> implementation
		after: '', // use cursor for old <InfiniteScroll> implementation
		popularTitlesFilter: { ...graphqlFilters.value, searchQuery: searchText.value },
		watchNowFilter: {
			packages: graphqlFilters.value.packages,
			monetizationTypes: graphqlFilters.value.monetizationTypes as MonetizationType[],
		},
		popularTitlesSortBy: sortBy.value,
		language: language.value,
		country: country.value,
		sortRandomSeed: randomSeed.value,
	};
});

const popularPageDataQuery = useQuery<GetPopularTitlesQuery, GetPopularTitlesQueryVariables>(
	GetPopularTitlesDocument,
	queryVariables,
	{
		fetchPolicy: 'cache-and-network',
		enabled: !isProviderPage.value,
	}
);
const popularPageData = computed(() => popularPageDataQuery.result.value?.popularTitles);

popularPageDataQuery.onResult(data => {
	isFirstPageLoaded.value = true;

	if (data && hasPaginationImplementation.value) {
		updatePaginationMeta(queryVariables.value.offset ?? 0);
	}
	if (data) {
		setPopularPageJsonLD(data.popularTitles);
	}
});
popularPageDataQuery.onError(e => {
	if (import.meta.client) {
		toastError({ message: t('WEBAPP_ALERT_ERROR_TITLE'), duration: 2000 });
	}

	captureMessageForSentry('[GraphQL Popular Page Data Query]:', { error: e, where: 'Component: Popular' }, 'error');
});
watch(
	() => isLoggedIn.value,
	() => popularPageDataQuery.refetch()
);

// Genres With Subgenres
const showSubgenresFilter = computed(() => activeSubgenreCountries.includes(country.value));

const genresWithSubgenresQuery = useQuery<GetOrderedSubgenresQuery, GetOrderedSubgenresQueryVariables>(
	GetOrderedSubgenresDocument,
	() => ({
		country: country.value,
		language: language.value,
	}),
	{ enabled: showSubgenresFilter.value }
);
const genresWithSubgenres = computed(() => genresWithSubgenresQuery.result.value);
const orderedSubgenres = computed(() => genresWithSubgenres.value?.genres);
const subgenresList = computed(() => genresWithSubgenres.value?.subgenres);

// Popular Page Metadata
const isSubgenreActive = computed(() => showSubgenresFilter.value && (filterPopular.value.subgenres?.length ?? 0) > 0);

const activeSubgenreTitle = computed(() => {
	if (!isSubgenreActive.value || filterPopular.value.subgenres?.length !== 1) return '';

	const activeSubgenre = filterPopular.value.subgenres[0];
	return subgenresList.value?.find(sub => sub.content.shortName === activeSubgenre)?.content.name ?? '';
});

const popularPageMetaDataQuery = useQuery<GetPopularMetaDataQuery, GetPopularMetaDataQueryVariables>(
	GetPopularMetaDataDocument,
	() => ({ fullPath: route.fullPath, site: activeDomain.value }),
	{ enabled: isSubgenreActive.value }
);
const popularPageMetaData = computed(() => popularPageMetaDataQuery.result.value?.metaData);

watch(
	() => activeRoute.value,
	route => {
		const { collectionType } = route?.meta as any;
		if (collectionType === CollectionType.POPULAR) {
			popularRoute.value = route;
		}
	},
	{ immediate: true }
);

// when leaving page through route
watch(
	() => activeRoute.value,
	() => {
		if (!activeRoute.value?.name?.includes('popular')) onLeavePopularPage();
	}
);

// Permanent Audiences for Subgenres
const previousPermanentAudience = ref('');

const head = computed(() => {
	// update meta title for subgenre pages - title count disparity from content
	if (!isSubgenreActive.value || !popularPageMetaData.value?.metaTitle || !titleAmount.value) return {};

	const metaTitleWithTitleCount = popularPageMetaData.value?.metaTitle.replace(
		'{{FE-TitleCount}}',
		String(titleAmount.value)
	);

	return {
		title: metaTitleWithTitleCount,
		meta: [
			{ vmid: 'title', name: 'title', content: metaTitleWithTitleCount },
			{ vmid: 'og:title', property: 'og:title', content: metaTitleWithTitleCount },
		],
	};
});

const permanentAudienceSubgenres = computed(() =>
	isSubgenreActive.value
		? (
				(subgenresList.value ?? []).find(sub => sub.content.shortName === filterPopular.value.subgenres![0])
					?.id ?? ''
		  ).replace('tl-pa-', 'pa:')
		: ''
);

const setPermanentAudienceSubgenre = computed(
	() =>
		isSubgenreActive.value &&
		!!permanentAudienceSubgenres.value &&
		// prevent duplicate events firing when non-subgenre filters are changed on Popular page
		permanentAudienceSubgenres.value !== previousPermanentAudience.value &&
		!!activeRoute.value?.name?.includes('popular')
);

watch(
	fullPath,
	() => {
		if (!isSubgenreActive.value) {
			resetPermanentAudiencesSubgenres();
			previousPermanentAudience.value = '';
		}
		if (setPermanentAudienceSubgenre.value) {
			setPermanentAudiencesSubgenres([permanentAudienceSubgenres.value]);
			previousPermanentAudience.value = permanentAudienceSubgenres.value;
		}
	},
	{ immediate: true }
);

// when leaving page through modal
onBeforeUnmount(() => onLeavePopularPage());

function onLeavePopularPage() {
	resetPermanentAudiencesSubgenres();
	previousPermanentAudience.value = '';
}

const isProviderPagesEnabled = computed(
	() => ['en', 'fr', 'de', 'it', 'pt', 'es', 'ar'].includes(language.value) && !isLoggedIn.value
);

// check if from last route and current route, there was a change in single-provider pages with its ProviderModule.
// this tells us that since the last route we needed to update the single-provider page: <ProviderModule>.
// either by adding it to the popular page, or removing it.
// watching this change lets us skip unnecessary rerenders.
const isProviderPageChanged = computed(() => {
	const lastPageIsProviderPage =
		!!lastRoute.value?.name?.startsWith('app.titles.popular.provider') && isProviderPagesEnabled.value;
	const currentPageIsProviderPage = isProviderPage.value;
	return lastPageIsProviderPage !== currentPageIsProviderPage;
});

const { providersByShortName } = storeToRefs(useConstantStore());
const selectedProvider = computed(() => providersByShortName.value[filterPopular.value.providers[0]]);

const selectedTab = computed(() => {
	if (isFilterActive('content_types', TitleObjectType.MOVIE, filterPopular.value)) {
		return PopularTabType.MOVIE;
	}

	if (isFilterActive('content_types', TitleObjectType.SHOW, filterPopular.value)) {
		return PopularTabType.SHOW;
	}

	return PopularTabType.ALL;
});

function getCurrentPage() {
	const page = (route.query.page as unknown as string) || '1';
	return parsePageNumber(page);
}

function search(searchQuery: string) {
	searchText.value = searchQuery;
}

const isLoading = computed(() => !isFirstPageLoaded.value && popularPageDataQuery.loading.value);

// PERCEIVED LOADING START
const perceivedLoading = ref(false);

// usually popularPageDataQuery wouldn't change when graphql requests have been requested already.
// but since we're fetching sponsoredAdQuery upon every pageview, popularPageDataQuery will change and perceivedLoading will change, too.
watch(
	() => isLoading.value,
	showingSpinner => {
		// just to be sure, that SSR is never rendering the loading state
		if (import.meta.server) return;

		// add a small time, so the vue runtime gets time to render the loading state.
		// if there is no loading state, vue will have to update the existing components with the new virtual DOM. this is more compute intense.
		if (showingSpinner) perceivedLoading.value = true;

		// using setTimeout instead of $nextTick, because $nextTick will be optimised by Vue into one render.
		// but we need to force an additional render with the loading state.
		if (!showingSpinner) setTimeout(() => (perceivedLoading.value = false), 0);
	}
);
// PERCEIVED LOADING START

const popularTitles = computed(() => popularPageData.value?.edges || []);
const titleAmount = computed(() => popularPageData.value?.totalCount ?? 0);
const isFirstPage = computed(() => !popularPageData.value?.pageInfo.hasPreviousPage);
const isAtEndOfList = computed(() => !popularPageData.value?.pageInfo.hasNextPage);

// SUBGENRES //

watch(filterPopular, (newValue: FilterCollection, oldValue: FilterCollection) => {
	if (!isProviderPage.value) searchText.value = '';

	if (!isEqual(newValue, oldValue)) {
		isFirstPageLoaded.value = false;

		// to separate `page` query param and only set `requestPage` when applicable and needed.
		// otherwise we'd do fresh request whenever the `page` query parameter changes (due to <InfiniteBiScroll>).
		requestPage.value = 1;
	}

	if (!isEqual(newValue.providers, oldValue.providers)) {
		searchText.value = '';
	}
});

const router = useRouter();
onMounted(() => {
	if (route.query['jw-pro']) handleStripeRedirect(route, router, jwLoginId.value);

	// track first page view with permanent Audiences here, after subgenreList fetched in beforeEnter routing/config
	if (setPermanentAudienceSubgenre.value && (activeRoute.value?.name || '').includes('subgenre')) {
		TrackingHelper.trackPage(fullPath.value);
		previousPermanentAudience.value = permanentAudienceSubgenres.value;
	}
});

// it's possible, that the `page` param is empty due to old <InfiniteScroll> implementation.
function load(event: any, page?: number | undefined, direction: 'prev' | 'next' = 'next') {
	if (event == null) return;

	const endCursor = hasPaginationImplementation.value ? null : popularPageData.value?.pageInfo.endCursor ?? null;
	const after = hasPaginationImplementation.value ? null : endCursor;
	const offset = hasPaginationImplementation.value
		? (page! - 1) * queryVariables.value.first!
		: !endCursor && direction === 'next'
		? popularTitles.value.length
		: null;

	const popularPageDataQueryFetchMore = popularPageDataQuery.fetchMore({
		variables: { offset, after },
		updateQuery(previousResult, { fetchMoreResult }) {
			if (!fetchMoreResult) return previousResult;
			if (!previousResult?.popularTitles) return fetchMoreResult;

			// either append or prepend the fetched titles
			const previousEdges = previousResult.popularTitles.edges ?? [];
			const fetchMoreEdges = fetchMoreResult.popularTitles.edges ?? [];

			// deduplicate by node ID to guard against the API returning the same items twice
			const previousIds = new Set(previousEdges.map(e => e.node.id));
			const uniqueFetchMoreEdges = fetchMoreEdges.filter(e => !previousIds.has(e.node.id));

			const edges =
				direction === 'next'
					? [...previousEdges, ...uniqueFetchMoreEdges]
					: [...uniqueFetchMoreEdges, ...previousEdges];

			return {
				popularTitles: {
					__typename: previousResult.popularTitles.__typename,
					edges,
					pageInfo: fetchMoreResult.popularTitles.pageInfo,
					totalCount: fetchMoreResult.popularTitles.totalCount,
				},
			};
		},
	});

	if (popularPageDataQueryFetchMore == null) return;

	popularPageDataQueryFetchMore
		.catch(e => {
			captureMessageForSentry(
				'[GraphQL Popular Page Load More Titles Issue]:',
				{ error: e, where: 'Component: Popular, method: onLoadNext' },
				'error'
			);
		})
		.finally(() => {
			if (hasPaginationImplementation.value) updatePaginationMeta(offset || 0);

			event.target?.complete?.(popularTitles.value.length);
		});
}

function trackClick(payload: TitleEventPayloadGraphql) {
	if (searchText.value) {
		TrackingHelper.trackEvent('search_suggest_click_lower', {
			action: getSelectedTab(activeRoute.value?.name as string),
			label: searchText.value,
			property: selectedTab.value,
		});
	}

	trackTitleEventGraphql('title_clicked', payload, 'popular-timeline');
}

function scrollToTop() {
	content.value?.scrollToTop(0, 'auto');
}

function updatePaginationMeta(offset: number) {
	const page = offset / queryVariables.value.first!;
	const prev = page > 1 ? page - 1 : null;
	const next = page + 1;
	const paginationData = { prev, next, page };
	setPagination(paginationData);
}

useHead(head);
</script>

<style scoped lang="scss">
@import '~@/styles/variables.scss';
@import '~@/styles/mixins.scss';

html {
	scroll-padding-top: 56px;
	@media screen and (max-width: 1050px) {
		scroll-padding-top: 105px;
	}
}

.popular-page__spinner {
	width: 100%;
	height: 100vh;
	display: flex;
	align-items: center;
	justify-content: center;
}

.overlay-blocking-element {
	position: absolute;
	height: 100vh;
	width: 100vw;
}
.content-provider-content {
	position: relative;
	width: 100vw;
	h1.section-title {
		font-size: 32px;
	}
	.content-header {
		@include sm() {
			max-height: 178px;
			&.content-header--expanded {
				max-height: none;
			}
		}
	}
	&.font-style-2 {
		.section-title {
			-webkit-font-smoothing: antialiased;
			font-family: Anton, Anton-Fallback, Impact;
			letter-spacing: 0.64px !important;
			font-weight: 400 !important;
		}
	}
}
.catalog-title {
	display: flex;
	align-items: center;
	justify-content: flex-start;

	width: 100%;
	h2 {
		@media (max-width: 1050px) {
			margin-inline-start: 15px;
		}
	}
}
.show-filters {
	margin: 0 15px;
	&__text {
		@include sm() {
			display: none;
		}
	}
}

.sponsored-recommendation {
	height: 100%;

	@media (max-width: 540px) {
		height: 270px;
	}
}

.list-options {
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: center;
	margin-bottom: 16px;
}

.filter-bar {
	margin-top: 24px;
}

#catalog-jumplink {
	position: relative;
	top: -50px;

	@media (max-width: 1050px) {
		top: -90px;
	}
}
</style>
